/**
 * FlowKnowledgeSection — the per-flow Knowledge area inside the flow editor.
 *
 * Lists the knowledge entries tagged for this branch (applies_to ⊇ slug, via
 * the /ion-knowledge?applies_to=<slug> filter) and lets an author create or
 * edit them with the shared KnowledgeEntryEditor. New entries are pre-tagged
 * (and locked) to the branch slug so they surface first in the assistant's
 * index whenever this flow is active. Review-gating is inherited from the
 * knowledge endpoints (can_publish_directly) — no separate machinery here.
 *
 * Shown only for a saved flow (a brand-new flow has no slug to tag against).
 */
import { useEffect, useState } from 'preact/hooks'

import { listKnowledgeEntries } from '../../../services/ionKnowledgeService.js'
import { KnowledgeEntryEditor } from '../AdminAiKnowledgeSubtab.jsx'
import { FlowHelp } from './FlowHelp.jsx'

export function FlowKnowledgeSection({ slug, canPublish = true }) {
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editing, setEditing] = useState(null) // null | 'new' | entry object
  const [notice, setNotice] = useState(null)

  const refresh = async () => {
    if (!slug) return
    setLoading(true)
    setError(null)
    try {
      const data = await listKnowledgeEntries({ includeInactive: true, appliesTo: slug })
      setEntries(data.entries || [])
    } catch (e) {
      setError(e?.message || 'network')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  return (
    <section className="admin-aim-section admin-flow-section admin-flow-knowledge">
      <div className="admin-flow-section__head">
        <h3 className="admin-aim-section-title">
          Knowledge (flow-specific) <FlowHelp k="knowledge" />
        </h3>
        <button
          type="button"
          className="admin-ai-btn admin-ai-btn--ghost admin-flow-add"
          onClick={() => setEditing('new')}
        >
          {canPublish ? '+ New entry' : '+ Propose entry'}
        </button>
      </div>
      <p className="admin-flow-hint">
        Knowledge entries tagged <code>{slug}</code>. Ion prioritises them in its
        knowledge index whenever this branch is active — use them for
        branch-specific guidance (rules of thumb, gotchas, how to weigh
        trade-offs). The body is fetched on-demand via <code>get_knowledge</code>.
      </p>

      {notice && (
        <div className="admin-aim-notice-banner" role="status">
          <span className="admin-aim-notice-text">{notice}</span>
          <button
            type="button"
            className="admin-aim-notice-close"
            aria-label="Dismiss"
            onClick={() => setNotice(null)}
          >
            ×
          </button>
        </div>
      )}
      {error && <div className="admin-aim-error-banner">{error}</div>}

      {loading && entries.length === 0 ? (
        <p className="admin-aim-empty admin-flow-empty">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="admin-aim-empty admin-flow-empty">
          No knowledge entries for this flow yet.
        </p>
      ) : (
        <ul className="admin-flow-list admin-flow-knowledge-list" role="list">
          {entries.map((e) => (
            <li
              key={e.topic_id}
              className={`admin-flow-knowledge-row${e.is_active ? '' : ' is-inactive'}`}
            >
              <div className="admin-flow-knowledge-row__main">
                <code>{e.topic_id}</code>
                <span className="admin-flow-knowledge-row__title">{e.title}</span>
                <span className="admin-flow-knowledge-row__summary">{e.summary}</span>
              </div>
              <div className="admin-flow-knowledge-row__meta">
                {!e.is_active && <span className="admin-aim-meta">inactive</span>}
                <button
                  type="button"
                  className="admin-ai-btn admin-ai-btn--ghost"
                  onClick={() => setEditing(e)}
                >
                  Edit
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <KnowledgeEntryEditor
          mode={editing === 'new' ? 'create' : 'edit'}
          initial={editing === 'new' ? null : editing}
          canPublish={canPublish}
          lockAppliesTo={slug}
          onClose={() => setEditing(null)}
          onSaved={(result) => {
            setEditing(null)
            if (result?.queued) {
              setNotice(result.message || 'Submitted for review.')
            }
            refresh()
          }}
        />
      )}
    </section>
  )
}
